import { useSwipeToDismiss } from "@lib/hooks/useSwipeToDismiss";
import { type Toast, useToastStore } from "@lib/stores/toast";
import { cn } from "@lib/utils/cn";
import { X } from "lucide-react";
import { TOAST_VARIANTS } from "./toastVariants";

export function ToastCard({ toast }: { toast: Toast }) {
  const variant = TOAST_VARIANTS[toast.type];
  const Icon = variant.icon;
  const removeToast = useToastStore((s) => s.removeToast);
  const pauseToast = useToastStore((s) => s.pauseToast);
  const resumeToast = useToastStore((s) => s.resumeToast);

  const swipe = useSwipeToDismiss({
    onDismiss: () => removeToast(toast.id),
  });

  return (
    <div
      className={cn(
        "toast-card",
        `toast-card-${toast.type}`,
        toast.exiting && "toast-card-exit",
        toast.morphing && "toast-card-morph",
      )}
      role={variant.role}
      aria-live={variant.ariaLive}
      aria-atomic="true"
      onPointerDown={swipe.onPointerDown}
      onPointerMove={swipe.onPointerMove}
      onPointerUp={swipe.onPointerUp}
      onMouseEnter={() => pauseToast(toast.id)}
      onMouseLeave={() => resumeToast(toast.id)}
    >
      <span className="toast-card-chip">
        <Icon size={14} strokeWidth={2} className={variant.spin ? "toast-icon-spin" : undefined} />
      </span>
      <div className="toast-card-body">
        <p className="toast-card-title">
          {toast.title}
          {toast.count > 1 && <span className="toast-card-count">&times;{toast.count}</span>}
        </p>
        {toast.message && <p className="toast-card-message">{toast.message}</p>}
        {toast.actions && toast.actions.length > 0 && (
          <div className="toast-card-actions">
            {toast.actions.map((a, i) => (
              <button
                key={i}
                type="button"
                className="toast-card-action-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  a.onClick();
                }}
              >
                {a.label}
              </button>
            ))}
          </div>
        )}
      </div>
      <button
        type="button"
        className="toast-card-close"
        aria-label="Fermer la notification"
        onClick={(e) => {
          e.stopPropagation();
          removeToast(toast.id);
        }}
      >
        <X size={11} strokeWidth={2} />
      </button>
      {toast.type !== "loading" && (
        <span className="toast-card-progress-track">
          <span
            className="toast-card-progress-bar"
            style={{ "--dur": `${toast.duration}ms` } as React.CSSProperties}
          />
        </span>
      )}
    </div>
  );
}
